import React, { useState, useMemo } from 'react';
import {
  TrendingUp, Download, RefreshCw, Eye, AlertTriangle,
  Users, Shield, Ship, FileText, ChevronRight, ChevronDown,
  AlertCircle, Clock, CheckCircle, XCircle, Sparkles, ExternalLink,
  ClipboardList, FileSearch, ListChecks
} from 'lucide-react';
// FT-356: back navigation moved to shell-level MobileBottomBar via
// NavigationBackContext. Header BackButton removed.
import { useSetBackHandler } from '../../../../contexts/NavigationBackContext';

// Compliance weight constants per brief
const COMPLIANCE_WEIGHTS = {
  crew: 0.25,      // 25% - Certificates, familiarisation, HoR
  equipment: 0.35, // 35% - Drills, LSA/FFE, defects
  vessel: 0.30,    // 30% - Vessel certificates, surveys
  sms: 0.10        // 10% - Document reviews, procedures
};

// Status thresholds
const STATUS_THRESHOLDS = {
  GREEN: 95,  // ≥95% = Green
  AMBER: 85   // 85-94% = Amber, <85% = Red
};

// Calculate category compliance score
const calculateCategoryScore = (compliant, total) => {
  if (total === 0) return 100; // No items = compliant
  return Math.round((compliant / total) * 100);
};

// Get status color based on score
const getStatusColor = (score) => {
  if (score >= STATUS_THRESHOLDS.GREEN) return 'green';
  if (score >= STATUS_THRESHOLDS.AMBER) return 'amber';
  return 'red';
};

// Get status label
const getStatusLabel = (score) => {
  if (score >= STATUS_THRESHOLDS.GREEN) return 'Compliant';
  if (score >= STATUS_THRESHOLDS.AMBER) return 'Attention Required';
  return 'Non-Compliant';
};

// Compliance Card Component - Clean white card design with colored left border
const ComplianceCard = ({ title, icon: Icon, score, weight, items, expanded, onToggle, color }) => {
  const statusColor = getStatusColor(score);
  const weightedContribution = Math.round(score * weight);

  // Clean card design: white background with colored LEFT border
  const borderColors = {
    green: 'border-l-4 border-l-green-600',
    amber: 'border-l-4 border-l-yellow-500',
    red: 'border-l-4 border-l-red-600'
  };

  const textColors = {
    green: 'text-green-700',
    amber: 'text-yellow-700',
    red: 'text-red-700'
  };

  const iconBgColors = {
    green: 'bg-green-100',
    amber: 'bg-yellow-100',
    red: 'bg-red-100'
  };

  const badgeColors = {
    green: 'bg-green-100 text-green-800',
    amber: 'bg-yellow-100 text-yellow-800',
    red: 'bg-red-100 text-red-800'
  };

  return (
    <div className={`bg-white shadow-sm rounded-lg ${borderColors[statusColor]} overflow-hidden`}>
      <button
        onClick={onToggle}
        className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors border-b border-gray-200"
      >
        <div className="flex items-center space-x-3">
          <div className={`p-2 rounded-lg ${iconBgColors[statusColor]}`}>
            <Icon className={`h-5 w-5 ${textColors[statusColor]}`} />
          </div>
          <div className="text-left">
            <h3 className="text-base font-medium text-gray-900">{title}</h3>
            <p className="text-xs text-gray-500">Weight: {Math.round(weight * 100)}%</p>
          </div>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-right">
            <span className={`px-2.5 py-0.5 rounded-full text-sm font-bold ${badgeColors[statusColor]}`}>
              {score}%
            </span>
            <div className="text-xs text-gray-500 mt-1">+{weightedContribution}pts</div>
          </div>
          {expanded ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400" />
          )}
        </div>
      </button>

      {expanded && items && items.length > 0 && (
        <div className="p-4">
          <div className="space-y-2">
            {items.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between text-sm py-1">
                <span className="text-gray-600">{item.label}</span>
                <span className={`font-medium ${
                  item.status === 'good' ? 'text-green-600' :
                  item.status === 'warning' ? 'text-yellow-600' :
                  item.status === 'bad' ? 'text-red-600' : 'text-gray-600'
                }`}>{item.value}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Critical Alerts Component - Clean white card with red left border
const CriticalAlerts = ({ alerts }) => {
  if (!alerts || alerts.length === 0) return null;

  return (
    <div className="bg-white shadow-sm rounded-lg border-l-4 border-red-600">
      <div className="px-5 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            <h3 className="text-base font-medium text-gray-900">
              Critical Alerts
            </h3>
          </div>
          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            {alerts.length} Detention Risk{alerts.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
      <div className="p-4 space-y-3">
        {alerts.map((alert, idx) => (
          <div key={idx} className="flex items-start space-x-3 p-3 rounded-md border border-red-200 bg-red-50">
            <XCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{alert.title}</p>
              <p className="text-xs text-gray-600 mt-0.5">{alert.message}</p>
              {alert.regulatory && (
                <p className="text-xs text-red-600 mt-1">Ref: {alert.regulatory}</p>
              )}
              {alert.action && (
                <p className="text-xs text-gray-500 mt-1 italic">Action: {alert.action}</p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Warnings Component - Clean white card with yellow left border
const WarningsPanel = ({ warnings }) => {
  if (!warnings || warnings.length === 0) return null;

  return (
    <div className="bg-white shadow-sm rounded-lg border-l-4 border-yellow-500">
      <div className="px-5 py-3 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2" />
            <h3 className="text-base font-medium text-gray-900">
              Warnings
            </h3>
          </div>
          <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            {warnings.length} Issue{warnings.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
      <div className="p-4 space-y-3">
        {warnings.slice(0, 5).map((warning, idx) => (
          <div key={idx} className="flex items-start space-x-3 p-3 rounded-md border border-yellow-200 bg-yellow-50">
            <Clock className="h-5 w-5 text-yellow-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">{warning.title}</p>
              <p className="text-xs text-gray-600 mt-0.5">{warning.message}</p>
              {warning.action && (
                <p className="text-xs text-gray-500 mt-1 italic">Action: {warning.action}</p>
              )}
            </div>
          </div>
        ))}
        {warnings.length > 5 && (
          <p className="text-xs text-yellow-700 text-center pt-2">
            +{warnings.length - 5} more warnings
          </p>
        )}
      </div>
    </div>
  );
};

// Overall Score Ring Component
const ScoreRing = ({ score, label }) => {
  const statusColor = getStatusColor(score);
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (score / 100) * circumference;

  const colors = {
    green: { stroke: '#22c55e', text: 'text-green-600' },
    amber: { stroke: '#eab308', text: 'text-yellow-600' },
    red: { stroke: '#ef4444', text: 'text-red-600' }
  };

  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <svg className="w-32 h-32 transform -rotate-90">
          <circle
            cx="64"
            cy="64"
            r="45"
            stroke="#e5e7eb"
            strokeWidth="8"
            fill="none"
          />
          <circle
            cx="64"
            cy="64"
            r="45"
            stroke={colors[statusColor].stroke}
            strokeWidth="8"
            fill="none"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            strokeLinecap="round"
            className="transition-all duration-500"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`text-3xl font-bold ${colors[statusColor].text}`}>{score}%</span>
          <span className="text-xs text-gray-500">{label}</span>
        </div>
      </div>
      <div className={`mt-2 px-3 py-1 rounded-full text-xs font-medium ${
        statusColor === 'green' ? 'bg-green-100 text-green-800' :
        statusColor === 'amber' ? 'bg-yellow-100 text-yellow-800' :
        'bg-red-100 text-red-800'
      }`}>
        {getStatusLabel(score)}
      </div>
    </div>
  );
};

// Main Compliance Reports Content Component
// FT-354a: onBack renders a mobile back button on <lg viewports so users can
// exit the Reports module back to the dashboard. Optional — desktop chrome
// keeps the sidebar visible so a back button would be redundant.
export const ComplianceReportsContent = ({ data, savedReports = [], onGenerateReport, onViewReport, onBack }) => {
  // FT-356: register onBack (if any) as the shell-level back handler.
  useSetBackHandler(onBack);
  const [expandedCards, setExpandedCards] = useState({
    crew: false,
    equipment: false,
    vessel: false,
    sms: false
  });

  // Calculate individual category scores
  const scores = useMemo(() => {
    if (!data) return null;

    // Crew score: certificates + familiarisation + HoR
    const crewCertCompliant = data.crew.certificates.total - data.crew.certificates.expired;
    const crewCertScore = calculateCategoryScore(crewCertCompliant, data.crew.certificates.total);
    const familiarisationScore = calculateCategoryScore(data.crew.familiarisation.completed, data.crew.familiarisation.total);
    const horTotal = data.crew.hoursOfRest.compliant + data.crew.hoursOfRest.violations;
    const horScore = calculateCategoryScore(data.crew.hoursOfRest.compliant, horTotal || 1);
    const crewScore = Math.round((crewCertScore * 0.5) + (familiarisationScore * 0.3) + (horScore * 0.2));

    // Equipment score: drills + equipment checks + defects
    const drillsCompliant = data.drills.total - data.drills.overdue;
    const drillsScore = calculateCategoryScore(drillsCompliant, data.drills.total);
    const equipmentChecksCompliant = data.equipment.total - data.equipment.overdue;
    const equipmentChecksScore = calculateCategoryScore(equipmentChecksCompliant, data.equipment.total);
    const defectsScore = data.defects.critical === 0 && data.defects.overdue === 0 ? 100 :
                         data.defects.critical > 0 ? 50 : 75;
    const equipmentScore = Math.round((drillsScore * 0.4) + (equipmentChecksScore * 0.4) + (defectsScore * 0.2));

    // Vessel score: vessel certificates (currently may be empty)
    const vesselCertCompliant = data.vessel.certificates.total - data.vessel.certificates.expired;
    const vesselScore = data.vessel.certificates.total > 0
      ? calculateCategoryScore(vesselCertCompliant, data.vessel.certificates.total)
      : 100; // No certs tracked = assumed compliant

    // SMS score: document reviews
    const docsCompliant = data.documents.total - data.documents.overdue;
    const smsScore = calculateCategoryScore(docsCompliant, data.documents.total);

    // Overall weighted score
    const overall = Math.round(
      (crewScore * COMPLIANCE_WEIGHTS.crew) +
      (equipmentScore * COMPLIANCE_WEIGHTS.equipment) +
      (vesselScore * COMPLIANCE_WEIGHTS.vessel) +
      (smsScore * COMPLIANCE_WEIGHTS.sms)
    );

    return {
      crew: crewScore,
      equipment: equipmentScore,
      vessel: vesselScore,
      sms: smsScore,
      overall
    };
  }, [data]);

  // Build card items for drill-down
  const cardItems = useMemo(() => {
    if (!data) return {};

    return {
      crew: [
        {
          label: 'Valid Certificates',
          value: `${data.crew.certificates.total - data.crew.certificates.expired}/${data.crew.certificates.total}`,
          status: data.crew.certificates.expired === 0 ? 'good' : 'bad'
        },
        {
          label: 'Expired Certificates',
          value: data.crew.certificates.expired,
          status: data.crew.certificates.expired === 0 ? 'good' : 'bad'
        },
        {
          label: 'Expiring Soon (30 days)',
          value: data.crew.certificates.expiringSoon,
          status: data.crew.certificates.expiringSoon === 0 ? 'good' : 'warning'
        },
        {
          label: 'Familiarisation Complete',
          value: `${data.crew.familiarisation.completed}/${data.crew.familiarisation.total}`,
          status: data.crew.familiarisation.completed === data.crew.familiarisation.total ? 'good' : 'warning'
        },
        {
          label: 'HoR Violations (7 days)',
          value: data.crew.hoursOfRest.violations,
          status: data.crew.hoursOfRest.violations === 0 ? 'good' : 'warning'
        }
      ],
      equipment: [
        {
          label: 'Drills Compliant',
          value: `${data.drills.total - data.drills.overdue}/${data.drills.total}`,
          status: data.drills.overdue === 0 ? 'good' : 'bad'
        },
        {
          label: 'Overdue Drills',
          value: data.drills.overdue,
          status: data.drills.overdue === 0 ? 'good' : 'bad'
        },
        {
          label: 'Drills Due Soon',
          value: data.drills.dueSoon,
          status: data.drills.dueSoon === 0 ? 'good' : 'warning'
        },
        {
          label: 'Equipment Checks Compliant',
          value: `${data.equipment.total - data.equipment.overdue}/${data.equipment.total}`,
          status: data.equipment.overdue === 0 ? 'good' : 'bad'
        },
        {
          label: 'Open Defects',
          value: data.defects.open,
          status: data.defects.open === 0 ? 'good' : 'warning'
        },
        {
          label: 'Critical Defects',
          value: data.defects.critical,
          status: data.defects.critical === 0 ? 'good' : 'bad'
        },
        {
          label: 'Overdue Defects',
          value: data.defects.overdue,
          status: data.defects.overdue === 0 ? 'good' : 'bad'
        }
      ],
      vessel: [
        {
          label: 'Valid Certificates',
          value: `${data.vessel.certificates.total - data.vessel.certificates.expired}/${data.vessel.certificates.total}`,
          status: data.vessel.certificates.expired === 0 ? 'good' : 'bad'
        },
        {
          label: 'Expired Certificates',
          value: data.vessel.certificates.expired,
          status: data.vessel.certificates.expired === 0 ? 'good' : 'bad'
        },
        {
          label: 'Expiring Soon (90 days)',
          value: data.vessel.certificates.expiringSoon,
          status: data.vessel.certificates.expiringSoon === 0 ? 'good' : 'warning'
        }
      ],
      sms: [
        {
          label: 'Documents Current',
          value: `${data.documents.total - data.documents.overdue}/${data.documents.total}`,
          status: data.documents.overdue === 0 ? 'good' : 'warning'
        },
        {
          label: 'Reviews Overdue',
          value: data.documents.overdue,
          status: data.documents.overdue === 0 ? 'good' : 'bad'
        },
        {
          label: 'Reviews Due Soon',
          value: data.documents.dueSoon,
          status: data.documents.dueSoon === 0 ? 'good' : 'warning'
        }
      ]
    };
  }, [data]);

  const toggleCard = (card) => {
    setExpandedCards(prev => ({
      ...prev,
      [card]: !prev[card]
    }));
  };

  if (!data || !scores) {
    return (
      <div className="space-y-6">
        {/* FT-356: Back navigation moved to shell-level MobileBottomBar. */}
        <div className="bg-white shadow rounded-lg p-12 text-center">
          <RefreshCw className="h-12 w-12 text-gray-400 mx-auto mb-4 animate-spin" />
          <p className="text-gray-500">Gathering compliance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* FT-356: Back navigation moved to shell-level MobileBottomBar. */}
      {/* Critical Alerts Section - Always visible at top */}
      {data.criticalAlerts && data.criticalAlerts.length > 0 && (
        <CriticalAlerts alerts={data.criticalAlerts} />
      )}

      {/* Overall Compliance Score with Generate Button */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="flex items-center space-x-6">
            <ScoreRing score={scores.overall} label="Overall" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">MCA Audit Readiness</h2>
              <p className="text-sm text-gray-500 mt-1">
                Weighted compliance score based on ISM/WBC3 priorities
              </p>
              <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                <span className="flex items-center">
                  <CheckCircle className="h-3 w-3 text-green-500 mr-1" />
                  ≥95% = Compliant
                </span>
                <span className="flex items-center">
                  <AlertTriangle className="h-3 w-3 text-yellow-500 mr-1" />
                  85-94% = Attention
                </span>
                <span className="flex items-center">
                  <XCircle className="h-3 w-3 text-red-500 mr-1" />
                  &lt;85% = Risk
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onGenerateReport}
            className="mt-4 md:mt-0 px-4 py-2 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 flex items-center justify-center"
          >
            <Download className="h-5 w-5 mr-2" />
            Generate Report
          </button>
        </div>
      </div>

      {/* 4-Card Compliance Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <ComplianceCard
          title="Crew Compliance"
          icon={Users}
          score={scores.crew}
          weight={COMPLIANCE_WEIGHTS.crew}
          items={cardItems.crew}
          expanded={expandedCards.crew}
          onToggle={() => toggleCard('crew')}
        />
        <ComplianceCard
          title="Equipment & Drills"
          icon={Shield}
          score={scores.equipment}
          weight={COMPLIANCE_WEIGHTS.equipment}
          items={cardItems.equipment}
          expanded={expandedCards.equipment}
          onToggle={() => toggleCard('equipment')}
        />
        <ComplianceCard
          title="Vessel Certificates"
          icon={Ship}
          score={scores.vessel}
          weight={COMPLIANCE_WEIGHTS.vessel}
          items={cardItems.vessel}
          expanded={expandedCards.vessel}
          onToggle={() => toggleCard('vessel')}
        />
        <ComplianceCard
          title="SMS Documentation"
          icon={FileText}
          score={scores.sms}
          weight={COMPLIANCE_WEIGHTS.sms}
          items={cardItems.sms}
          expanded={expandedCards.sms}
          onToggle={() => toggleCard('sms')}
        />
      </div>

      {/* Warnings Section */}
      {data.warnings && data.warnings.length > 0 && (
        <WarningsPanel warnings={data.warnings} />
      )}

      {/* Quick Stats Summary */}
      <div className="bg-white shadow rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-3">Quick Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="text-xl font-bold text-gray-900">{data.crew.total}</div>
            <div className="text-xs text-gray-500">Active Crew</div>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="text-xl font-bold text-gray-900">{data.drills.total}</div>
            <div className="text-xs text-gray-500">Required Drills</div>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="text-xl font-bold text-gray-900">{data.defects.open}</div>
            <div className="text-xs text-gray-500">Open Defects</div>
          </div>
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="text-xl font-bold text-gray-900">{data.incidents.year}</div>
            <div className="text-xs text-gray-500">Incidents YTD</div>
          </div>
        </div>
      </div>

      {/* SMS Audit Portal Upsell */}
      <div className="bg-gradient-to-br from-cyan-50 to-blue-50 border border-cyan-200 rounded-lg p-5">
        <div className="flex items-start gap-4">
          <div className="p-2 bg-cyan-100 rounded-lg">
            <Sparkles className="h-6 w-6 text-cyan-600" />
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-cyan-900">SMS Audit & Management Portal</h4>
            <p className="text-sm text-cyan-700 mt-1 mb-4">
              Premium users get access to the full FleetSkipper Portal for comprehensive
              audit management, including:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
              <div className="flex items-center gap-2 text-sm text-cyan-800">
                <ClipboardList className="h-4 w-4 text-cyan-600" />
                <span>Internal Audits</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-cyan-800">
                <FileSearch className="h-4 w-4 text-cyan-600" />
                <span>Findings Register</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-cyan-800">
                <ListChecks className="h-4 w-4 text-cyan-600" />
                <span>Verification Matrix</span>
              </div>
            </div>
            <a
              href="https://sms-webapp.vercel.app"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-600 text-white text-sm font-medium rounded-lg hover:bg-cyan-700 transition-colors"
            >
              Open SMS Portal
              <ExternalLink className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>

      {/* Saved Reports History */}
      {savedReports.length > 0 && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b">
            <h4 className="text-lg font-medium text-gray-900">Report History</h4>
          </div>
          <div className="divide-y divide-gray-200">
            {savedReports
              .sort((a, b) => new Date(b.generatedAt) - new Date(a.generatedAt))
              .slice(0, 5)
              .map(report => (
                <div key={report.id} className="px-6 py-4 hover:bg-gray-50">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        Compliance Report - {report.id}
                      </p>
                      <p className="text-sm text-gray-500">
                        Generated: {new Date(report.generatedAt).toLocaleString()}
                      </p>
                    </div>
                    <button
                      onClick={() => onViewReport && onViewReport(report)}
                      className="text-cyan-600 hover:text-cyan-900"
                    >
                      <Eye className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}
    </div>
  );
};
